import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown } from './Icons';

const SearchableDropdown = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  disabled = false,
  className = '',
  displayKey = 'description',
  valueKey = 'id',
  loading = false,
  maxDisplayItems = 100, // Limit displayed items for performance
  showMoreText = 'Show more...',
  onSearch = null, // Callback for dynamic search
  searchDelay = 300, // Delay in ms before triggering search
  dropdownWidth = 'w-full', // Width of the dropdown
  displayFormat = 'simple', // 'simple' or 'detailed' for product display
  selectedDisplayKey = null, // Key to display in the selected field (different from dropdown display)
  showValue = true, // print the option's value after its label, e.g. "ACME (12)"
  // Pass a handler to let the reader add a value that is not in the list. A row
  // offering to add whatever they typed appears under the search box, which is
  // the only way to enter a unit the list does not carry.
  onCreate = null,
  createLabel = 'Add'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update filtered options when options change (for dynamic search)
  useEffect(() => {
    if (searchTerm) {
      // For both dynamic and local search, filter the options
      const filtered = options.filter(option =>
        option[displayKey].toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option[valueKey] && option[valueKey].toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredOptions(filtered);
    } else {
      // No search term, show all options
      setFilteredOptions(options);
    }
  }, [options, searchTerm, displayKey, valueKey]);

  // Handle search with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm && onSearch) {
      // If we have a dynamic search function, use it
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(() => {
        onSearch(searchTerm);
        setIsSearching(false);
      }, searchDelay);
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, onSearch, searchDelay]);

  // Limit displayed items for performance
  const displayOptions = filteredOptions.slice(0, maxDisplayItems);
  const hasMoreItems = filteredOptions.length > maxDisplayItems;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const typed = searchTerm.trim();
  const canCreate = Boolean(
    onCreate && typed &&
    !options.some(option => String(option[displayKey] ?? '').toLowerCase() === typed.toLowerCase())
  );

  const handleCreate = () => {
    onCreate(typed);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find(option => option[valueKey] === value);

  // Determine what to display in the selected field
  const getSelectedDisplay = () => {
    if (!selectedOption) return placeholder;
    if (selectedDisplayKey) {
      return selectedOption[selectedDisplayKey] || selectedOption[displayKey];
    }
    return selectedOption[displayKey];
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full px-3 py-2.5 text-left border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 ${
          disabled ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : 'bg-white border-slate-300 hover:border-slate-400 cursor-pointer'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={`truncate ${selectedOption ? 'text-slate-900' : 'text-slate-400'}`}>
            {getSelectedDisplay()}
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className={`absolute z-50 ${dropdownWidth} mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/60 max-h-60 overflow-hidden`}>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500"
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-left text-sm font-medium text-amber-700 hover:bg-amber-50 focus:bg-amber-50 focus:outline-none"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">+</span>
                <span className="truncate">{createLabel} &ldquo;{searchTerm.trim()}&rdquo;</span>
              </button>
            )}
            {loading || isSearching ? (
              <div className="p-3 text-center text-sm text-slate-500">
                {isSearching ? 'Searching...' : 'Loading...'}
              </div>
            ) : displayOptions.length === 0 ? (
              <div className="p-3 text-center text-sm text-slate-500">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              <>
                {displayOptions.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-3 py-2.5 text-left text-sm hover:bg-amber-50 focus:outline-none focus:bg-amber-50 transition-colors ${
                      value === option[valueKey] ? 'bg-amber-50 text-amber-900 font-medium' : 'text-slate-700'
                    }`}
                  >
                    {displayFormat === 'detailed' && option.product_name ? (
                      <div className="flex justify-between items-center">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">{option.product_name}</div>
                          <div className="text-xs text-slate-500">SKU: {option.sku}</div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="font-medium text-slate-900">Rs. {option.unit_price?.toLocaleString() || 0}</div>
                          <div className="text-xs text-slate-500">Stock: {option.stock_quantity || 0}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="truncate">
                        {option[displayKey]}
                        {showValue && option[valueKey] && option[valueKey] !== option[displayKey] && (
                          <span className="text-slate-400 ml-2">({option[valueKey]})</span>
                        )}
                      </div>
                    )}
                  </button>
                ))}

                {hasMoreItems && (
                  <div className="p-2 text-center text-slate-500 text-xs border-t border-slate-100">
                    {filteredOptions.length - maxDisplayItems} more items available.
                    <br />Use search to narrow down results.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
